import React, { Suspense, useCallback, useEffect, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import httpService from "../../services/httpService";
import { Provider } from "../../types/provider";

const ProviderProfilePage = React.lazy(
  () => import("../provider/ProviderProfilePage/ProviderProfilePage")
);

const ProfileEditorSkeleton: React.FC = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-rp-separator dark:bg-rp-surface">
    <div className="h-6 w-48 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
    <div className="mt-4 h-80 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800" />
  </div>
);

const ProviderProfilesPage: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState<string | null>(null);

  const loadProviders = useCallback(async () => {
    setProvidersLoading(true);
    setProvidersError(null);
    try {
      const response = await httpService.get<{ providers: Provider[] }>("/providers");
      setProviders(
        [...(response.data.providers ?? [])].sort((first, second) =>
          first.name.localeCompare(second.name)
        )
      );
    } catch (error) {
      setProviders([]);
      setProvidersError(error instanceof Error ? error.message : "Failed to load providers.");
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  return (
    <AdminPageLayout
      title="Provider Profiles"
      description="Create, edit, and approve a provider's public profile."
    >
      <div className="mx-auto max-w-[1100px] space-y-4">
        {providersError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-700 dark:text-red-400">{providersError}</p>
            <button
              type="button"
              onClick={loadProviders}
              className="mt-2 text-sm font-semibold text-red-700 underline dark:text-red-400"
            >
              Retry
            </button>
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-rp-separator dark:bg-rp-surface">
          <label
            htmlFor="provider-profile-provider"
            className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
          >
            Provider
          </label>
          <select
            id="provider-profile-provider"
            value={selectedProviderId}
            onChange={event => setSelectedProviderId(event.target.value)}
            disabled={providersLoading}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white"
          >
            <option value="">
              {providersLoading ? "Loading providers..." : "Select a provider"}
            </option>
            {providers.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.name} (ID {provider.id})
              </option>
            ))}
          </select>
        </div>

        {!selectedProviderId ? (
          <div className="rounded-2xl border border-gray-200 bg-white py-24 text-center dark:border-rp-separator dark:bg-rp-surface">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a provider to load or create its profile.
            </p>
          </div>
        ) : (
          <Suspense fallback={<ProfileEditorSkeleton />}>
            <ProviderProfilePage
              key={selectedProviderId}
              providerIdOverride={selectedProviderId}
              adminReviewMode
              embedded
            />
          </Suspense>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default ProviderProfilesPage;
